import React, { useState, useEffect } from 'react';
import { Activity, Info, Zap, Settings as SettingsIcon, Lightbulb, AlertTriangle, CheckCircle } from 'lucide-react';
import { getZoneColor, getZoneName } from '../../utils/hrZoneUtils';
import { light } from '../../theme/palette';
import { getApiBaseUrl } from '../../utils/api';
import { storage } from '../../services/storage';
import './HRZonesDrawer.css';

const API_BASE_URL = getApiBaseUrl();

interface HRZonesDrawerProps {
  onClose: () => void;
}

interface GarminHRValues {
  max_hr: number;
  resting_hr: number;
}

interface ZoneComparison {
  cedar: {
    zone_1_min: number;
    zone_1_max: number;
    zone_2_min: number;
    zone_2_max: number;
    zone_3_min: number;
    zone_3_max: number;
    zone_4_min: number;
    zone_4_max: number;
    zone_5_min: number;
    zone_5_max: number;
  };
  garmin: {
    zone_1_min: number;
    zone_1_max: number;
    zone_2_min: number;
    zone_2_max: number;
    zone_3_min: number;
    zone_3_max: number;
    zone_4_min: number;
    zone_4_max: number;
    zone_5_min: number;
    zone_5_max: number;
  };
}

interface SettingsData {
  current_zones: any;
  garmin_connected: boolean;
  garmin_device_name: string | null;
  garmin_last_sync: string | null;
  garmin_hr_values: GarminHRValues | null;
  zone_comparison: ZoneComparison | null;
  user_age: number | null;
}

export const HRZonesDrawer: React.FC<HRZonesDrawerProps> = ({ onClose }) => {
  // UI State
  const [view, setView] = useState<'default' | 'advanced'>('default');
  const [selectedModel, setSelectedModel] = useState<'cedar' | 'garmin'>('cedar');
  const [showComparison, setShowComparison] = useState(false);
  
  // Data State
  const [settingsData, setSettingsData] = useState<SettingsData | null>(null);
  const [maxHr, setMaxHr] = useState<string>('');
  const [restingHr, setRestingHr] = useState<string>('');
  
  // Manual zone entry (advanced mode only)
  const [zone1Min, setZone1Min] = useState<string>('');
  const [zone1Max, setZone1Max] = useState<string>('');
  const [zone2Min, setZone2Min] = useState<string>('');
  const [zone2Max, setZone2Max] = useState<string>('');
  const [zone3Min, setZone3Min] = useState<string>('');
  const [zone3Max, setZone3Max] = useState<string>('');
  const [zone4Min, setZone4Min] = useState<string>('');
  const [zone4Max, setZone4Max] = useState<string>('');
  const [zone5Min, setZone5Min] = useState<string>('');
  const [zone5Max, setZone5Max] = useState<string>('');
  
  // UI State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadSettingsData();
  }, []);

  const loadSettingsData = async () => {
    try {
      const token = await storage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/hr-zones/settings-data`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data: SettingsData = await response.json();
        setSettingsData(data);
        
        // If user has zones, populate fields
        if (data.current_zones) {
          setMaxHr(data.current_zones.max_heart_rate?.toString() || '');
          setRestingHr(data.current_zones.resting_heart_rate?.toString() || '');
          setSelectedModel(data.current_zones.zone_system || 'cedar');
          
          // Populate manual zone fields
          setZone1Min(data.current_zones.zone_1_min?.toString() || '');
          setZone1Max(data.current_zones.zone_1_max?.toString() || '');
          setZone2Min(data.current_zones.zone_2_min?.toString() || '');
          setZone2Max(data.current_zones.zone_2_max?.toString() || '');
          setZone3Min(data.current_zones.zone_3_min?.toString() || '');
          setZone3Max(data.current_zones.zone_3_max?.toString() || '');
          setZone4Min(data.current_zones.zone_4_min?.toString() || '');
          setZone4Max(data.current_zones.zone_4_max?.toString() || '');
          setZone5Min(data.current_zones.zone_5_min?.toString() || '');
          setZone5Max(data.current_zones.zone_5_max?.toString() || '');
        }
        // If Garmin connected, use their values
        else if (data.garmin_hr_values) {
          setMaxHr(data.garmin_hr_values.max_hr.toString());
          setRestingHr(data.garmin_hr_values.resting_hr?.toString() || '');
        }
        // Otherwise use age estimate
        else if (data.user_age) {
          const estimatedMax = 220 - data.user_age;
          setMaxHr(estimatedMax.toString());
        }
      }
    } catch (err) {
      console.log('Error loading settings data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncFromGarmin = async () => {
    if (!settingsData?.garmin_hr_values) return;
    
    setMaxHr(settingsData.garmin_hr_values.max_hr.toString());
    setRestingHr(settingsData.garmin_hr_values.resting_hr?.toString() || '');
    setSuccess(true);
    setTimeout(() => setSuccess(false), 2000);
  };

  const handleSave = async () => {
    setError('');
    setSuccess(false);
    setSaving(true);

    try {
      if (view === 'default') {
        // Default mode: Max HR + Resting HR with model selection
        const maxHrNum = parseInt(maxHr);
        const restingHrNum = parseInt(restingHr);

        if (!maxHrNum) {
          setError('Please enter Max HR');
          setSaving(false);
          return;
        }

        if (restingHrNum && maxHrNum <= restingHrNum) {
          setError('Max HR must be greater than Resting HR');
          setSaving(false);
          return;
        }

        if (maxHrNum < 120 || maxHrNum > 220) {
          setError('Max HR should be between 120-220 bpm');
          setSaving(false);
          return;
        }

        if (restingHrNum && (restingHrNum < 30 || restingHrNum > 100)) {
          setError('Resting HR should be between 30-100 bpm');
          setSaving(false);
          return;
        }

        // Determine source
        let source = 'manual';
        if (settingsData?.garmin_hr_values && 
            maxHrNum === settingsData.garmin_hr_values.max_hr &&
            restingHrNum === settingsData.garmin_hr_values.resting_hr) {
          source = 'garmin';
        }

        // Send to backend with model selection
        const token = await storage.getItem('access_token');
        const response = await fetch(`${API_BASE_URL}/api/hr-zones`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            max_heart_rate: maxHrNum,
            resting_heart_rate: restingHrNum || null,
            zone_system: selectedModel,
            source
          })
        });

        if (!response.ok) {
          throw new Error(`Failed to save HR zones: ${response.statusText}`);
        }

        setSuccess(true);
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        // Advanced mode: Manual zone entry
        const zones = {
          zone_1_min: parseInt(zone1Min),
          zone_1_max: parseInt(zone1Max),
          zone_2_min: parseInt(zone2Min),
          zone_2_max: parseInt(zone2Max),
          zone_3_min: parseInt(zone3Min),
          zone_3_max: parseInt(zone3Max),
          zone_4_min: parseInt(zone4Min),
          zone_4_max: parseInt(zone4Max),
          zone_5_min: parseInt(zone5Min),
          zone_5_max: parseInt(zone5Max)
        };

        // Basic validation
        if (Object.values(zones).some(v => isNaN(v) || v <= 0)) {
          setError('Please enter valid BPM values for all zones');
          setSaving(false);
          return;
        }

        // Send to backend
        const token = await storage.getItem('access_token');
        const response = await fetch(`${API_BASE_URL}/api/hr-zones`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            zones,
            zone_system: 'cedar', // Manual entry always uses cedar system
            source: 'manual'
          })
        });

        if (!response.ok) {
          throw new Error(`Failed to save HR zones: ${response.statusText}`);
        }

        setSuccess(true);
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save HR zones');
    } finally {
      setSaving(false);
    }
  };

  const renderZoneComparison = () => {
    if (!showComparison || !settingsData?.zone_comparison) return null;
    
    const { cedar, garmin } = settingsData.zone_comparison;
    
    return (
      <div className="hr-comparison-container">
        <h3 className="hr-comparison-title">Zone Comparison</h3>

        <div className="hr-flex-column-sm">
          {[1, 2, 3, 4, 5].map(zone => (
            <div key={zone} className="hr-comparison-row">
              <div className="hr-comparison-label">
                <span className="hr-zone-dot" style={{ backgroundColor: getZoneColor(light, zone) }} />
                Z{zone}
              </div>
              <div className="hr-comparison-values">
                <div className={`hr-comparison-value ${selectedModel === 'cedar' ? 'selected' : 'unselected'}`}>
                  Cedar: {cedar[`zone_${zone}_min` as keyof typeof cedar]}-{cedar[`zone_${zone}_max` as keyof typeof cedar]}
                </div>
                <div className={`hr-comparison-value ${selectedModel === 'garmin' ? 'selected' : 'unselected'}`}>
                  Garmin: {garmin[`zone_${zone}_min` as keyof typeof garmin]}-{garmin[`zone_${zone}_max` as keyof typeof garmin]}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <p className="hr-comparison-note">
          Cedar zones (Karvonen) are more personalized and typically higher. Garmin zones (% Max HR) match your watch's default settings.
        </p>
      </div>
    );
  };

  return (
    <div className="drawer-content">
      <h2 className="drawer-title">Heart Rate Training Zones</h2>
      
      {loading ? (
        <div className="hr-loading">
          <div className="hr-loading-spinner"></div>
          <p className="hr-loading-text">Loading zones...</p>
        </div>
      ) : (
        <>
          {/* Garmin Sync Status (if connected) */}
          {settingsData?.garmin_connected && (
            <div className="hr-status-box">
              <div className="hr-info-box-header">
                <Activity className="hr-icon-md hr-info-box-icon" />
                <div className="hr-info-box-content">
                  <p className="hr-info-box-title">
                    {settingsData.garmin_device_name || 'Garmin'} Connected
                  </p>
                  {settingsData.garmin_hr_values ? (
                    <>
                      <p className="hr-info-box-text">
                        Synced HR: Max <strong>{settingsData.garmin_hr_values.max_hr}</strong> bpm
                        {settingsData.garmin_hr_values.resting_hr && (
                          <>, Resting <strong>{settingsData.garmin_hr_values.resting_hr}</strong> bpm</>
                        )}
                      </p>
                      <button
                        type="button"
                        onClick={handleSyncFromGarmin}
                        className="hr-link-button"
                      >
                        Use These Values →
                      </button>
                    </>
                  ) : (
                    <p className="hr-info-box-text">
                      Waiting for HR data sync...
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Current Zones Status */}
          {settingsData?.current_zones && (
            <div className="hr-status-box hr-status-success">
              <div className="hr-info-box-header">
                <Zap className="hr-icon-sm hr-info-box-icon" />
                <p className="hr-info-box-text">
                  <strong>{settingsData.current_zones.zone_system === 'cedar' ? 'Karvonen' : 'Garmin'} zones</strong> active
                  {/* Truthful provenance. `source='garmin'` is reserved for a
                      future Garmin Connect user-profile sync (no endpoint
                      today). All current "auto" zones are Cedar-computed with
                      a source_detail explaining the anchor. */}
                  {settingsData.current_zones.source === 'garmin' && ' (synced from Garmin Connect)'}
                  {settingsData.current_zones.source === 'manual' && ' (manually configured)'}
                  {settingsData.current_zones.source === 'cedar_calculated' &&
                    settingsData.current_zones.source_detail === 'from_lt_hr_inference' &&
                    ' (estimated from your Garmin lactate threshold HR)'}
                  {settingsData.current_zones.source === 'cedar_calculated' &&
                    settingsData.current_zones.source_detail === 'from_observed_peak' &&
                    ' (calculated from your device-observed max HR)'}
                  {settingsData.current_zones.source === 'cedar_calculated' &&
                    settingsData.current_zones.source_detail === 'from_age_formula' &&
                    ' (formula estimate from age — connect a device to refine)'}
                  {settingsData.current_zones.source === 'cedar_calculated' &&
                    !settingsData.current_zones.source_detail &&
                    ' (Cedar calculated from your data)'}
                  {settingsData.current_zones.source === 'formula_estimate' && ' (formula estimate)'}
                  {settingsData.current_zones.source === 'calculated' && ' (calculated from your data)'}
                </p>
              </div>
            </div>
          )}

          {/* Education Section */}
          <div className="hr-info-box hr-section-spacing-lg">
            <div className="hr-info-box-header">
              <Info className="hr-icon-md hr-info-box-icon" />
              <div className="hr-info-box-content">
                <p className="hr-info-box-title">
                  Which Zone Model Should You Use?
                </p>
                <div className="hr-info-box-text hr-flex-column">
                  <div>
                    <strong>Karvonen (Recommended):</strong> More personalized, uses your Heart Rate Reserve. Research-backed for training accuracy. Zones will be higher than standard % Max HR.
                  </div>
                  <div>
                    <strong>Garmin (% Max HR):</strong> Matches your watch's default zones. Simpler calculation. Choose this if you want Cedar's targets to match your watch's zone display.
                  </div>
                  <div className="hr-info-box-detail">
                    <strong><Lightbulb className="hr-icon-inline" /> Key Point:</strong> Cedar always pushes <em>absolute BPM targets</em> (e.g., "130-142 bpm") to your watch. The model choice determines what those BPM values are.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Zone Model Selection */}
          <div className="hr-section-spacing">
            <label className="hr-input-label">
              Zone Calculation Model
            </label>
            <div className="hr-model-selector">
              <button
                type="button"
                className={`hr-model-button ${selectedModel === 'cedar' ? 'selected' : ''}`}
                onClick={() => setSelectedModel('cedar')}
              >
                <div className="hr-model-button-title">Karvonen</div>
                <div className="hr-model-button-subtitle">
                  Personalized, research-backed
                </div>
              </button>
              <button
                type="button"
                className={`hr-model-button ${selectedModel === 'garmin' ? 'selected' : ''}`}
                onClick={() => setSelectedModel('garmin')}
              >
                <div className="hr-model-button-title">Garmin (% Max)</div>
                <div className="hr-model-button-subtitle">
                  Matches watch default
                </div>
              </button>
            </div>
          </div>

          {/* HR Values Input */}
          <div className="hr-section-spacing">
            <div className="hr-input-group">
              <label className="hr-input-label">
                Max Heart Rate (bpm)
                {settingsData?.garmin_hr_values && (
                  <span className="hr-input-hint">
                    (Garmin: {settingsData.garmin_hr_values.max_hr})
                  </span>
                )}
              </label>
              <input
                type="number"
                value={maxHr}
                onChange={(e) => setMaxHr(e.target.value)}
                className="hr-input"
                placeholder="178"
                disabled={view === 'advanced'}
              />
            </div>

            <div className="hr-input-group">
              <label className="hr-input-label">
                Resting Heart Rate (bpm)
                {settingsData?.garmin_hr_values?.resting_hr && (
                  <span className="hr-input-hint">
                    (Garmin: {settingsData.garmin_hr_values.resting_hr})
                  </span>
                )}
                <span className="hr-input-hint">(optional for Garmin model)</span>
              </label>
              <input
                type="number"
                value={restingHr}
                onChange={(e) => setRestingHr(e.target.value)}
                className="hr-input"
                placeholder="60"
                disabled={view === 'advanced'}
              />
            </div>
          </div>

          {/* Zone Comparison Toggle */}
          {settingsData?.zone_comparison && (
            <button
              type="button"
              onClick={() => setShowComparison(!showComparison)}
              className="hr-button-toggle"
            >
              {showComparison ? '▲ Hide' : '▼ Show'} Zone Comparison (Cedar vs Garmin)
            </button>
          )}

          {renderZoneComparison()}

          {/* Advanced Mode Toggle */}
          <button
            type="button"
            onClick={() => setView(view === 'default' ? 'advanced' : 'default')}
            className="hr-button-toggle"
          >
            <SettingsIcon className="hr-icon-xs" />
            {view === 'default' ? 'Switch to Advanced Mode (Manual Zone Entry)' : 'Switch to Simple Mode'}
          </button>

          {/* Advanced Mode: Manual Zone Entry */}
          {view === 'advanced' && (
            <div className="hr-advanced-container">
              <p className="hr-advanced-warning">
                <AlertTriangle className="hr-icon-inline" /> Advanced: Enter exact BPM values for each zone
              </p>

              <div className="hr-flex-column-sm">
                {[
                  { zone: 1, min: zone1Min, max: zone1Max, setMin: setZone1Min, setMax: setZone1Max },
                  { zone: 2, min: zone2Min, max: zone2Max, setMin: setZone2Min, setMax: setZone2Max },
                  { zone: 3, min: zone3Min, max: zone3Max, setMin: setZone3Min, setMax: setZone3Max },
                  { zone: 4, min: zone4Min, max: zone4Max, setMin: setZone4Min, setMax: setZone4Max },
                  { zone: 5, min: zone5Min, max: zone5Max, setMin: setZone5Min, setMax: setZone5Max },
                ].map(({ zone, min: zoneMin, max: zoneMax, setMin: setZoneMin, setMax: setZoneMax }) => {
                  return (
                    <div key={zone} className="hr-zone-row">
                      <div className="hr-zone-label">
                        <span className="hr-zone-dot" style={{ backgroundColor: getZoneColor(light, zone) }} />
                        Z{zone} {getZoneName(zone)}
                      </div>
                      <input
                        type="number"
                        value={zoneMin}
                        onChange={(e) => setZoneMin(e.target.value)}
                        className="hr-zone-input"
                        placeholder="Min"
                      />
                      <input
                        type="number"
                        value={zoneMax}
                        onChange={(e) => setZoneMax(e.target.value)}
                        className="hr-zone-input"
                        placeholder="Max"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tips */}
          <div className="hr-info-box hr-section-spacing">
            <p className="hr-info-box-text">
              <strong><Lightbulb className="hr-icon-inline" /> Tip:</strong> You can also tell Cedar your zones in chat:
              "My max HR is 178 and resting is 60" or "My zone 2 is 130-142 bpm"
            </p>
          </div>

          {error && (
            <div className="hr-message-error">
              <p className="hr-message-error-text">{error}</p>
            </div>
          )}

          {success && (
            <div className="hr-message-success">
              <p className="hr-message-success-text"><CheckCircle className="hr-icon-inline hr-icon-success" /> HR zones saved successfully!</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="hr-actions">
            <button
              type="button"
              onClick={onClose}
              className="hr-button-cancel"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="hr-button-save"
              disabled={saving || (view === 'default' && !maxHr)}
            >
              {saving ? 'Saving...' : 'Save Zones'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};
